/**
 * Ant Design theme — Synaxion 13-ui-design Phase B (+ Z5 component tokens)
 * CSS semantic/primitive/component vars (src/index.css) → ConfigProvider token
 *
 * @see src/shared/constants/theme.ts — ANT_DESIGN_TOKEN_CSS_VAR_MAP
 */
import { theme as antdTheme, type ThemeConfig } from 'antd';
import { ANT_DESIGN_TOKEN_CSS_VAR_MAP } from '@shared/constants/theme';
import type { BrandIdentity } from '@shared/site/design/brand-identity';
import { mapBrandToBusinessThemeTokens } from '@shared/site/design/map-brand-to-business-theme';

export interface CreateAntdThemeFromCssVarsOptions {
  brand?: BrandIdentity;
}

const FALLBACK = {
  primary: '#5eead4',
  success: '#52c41a',
  warning: '#faad14',
  error: '#f5222d',
  radius: 12,
  radiusLg: 16,
  surfaceSubtle: '#0a0f2b',
  surfaceCanvas: '#0d1235',
  surface03: 'rgba(255, 255, 255, 0.03)',
  surface04: 'rgba(255, 255, 255, 0.04)',
  surface06: 'rgba(255, 255, 255, 0.06)',
  layoutBg: '#06081a',
  layoutSecondary: '#0a0f2b',
  textPrimary: '#f1f4ff',
  textMuted: '#b8c1e6',
  textQuaternary: '#7a86b8',
  colorLink: '#5eead4',
  colorLinkHover: '#7ef0e0',
  colorLinkActive: '#4dd4c0',
  borderDefault: 'rgba(255,255,255,0.08)',
  overlayScrim: 'rgba(6, 8, 26, 0.75)',
  controlHeight: 36,
  controlHeightSm: 28,
  controlHeightLg: 44,
  fontSize: 14,
  fontSizeSm: 13,
  lineHeight: 1.5,
  motionDurationMid: '200ms',
};

function readVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

function readPxVar(cssVar: string, fallbackPx: number): number {
  const raw = readVar(cssVar, `${fallbackPx}px`);
  const parsed = Number.parseFloat(raw.replace(/px$/, '').trim());
  return Number.isFinite(parsed) ? parsed : fallbackPx;
}

function readNumberVar(cssVar: string, fallback: number): number {
  const raw = readVar(cssVar, String(fallback));
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function createAntdThemeFromCssVars(
  options?: CreateAntdThemeFromCssVarsOptions
): ThemeConfig {
  const brandMerge = options?.brand ? mapBrandToBusinessThemeTokens(options.brand) : undefined;
  const map = ANT_DESIGN_TOKEN_CSS_VAR_MAP;
  const surface03 = readVar('--surface-glass-03', FALLBACK.surface03);
  const surface04 = readVar('--surface-glass-04', FALLBACK.surface04);
  const surface06 = readVar('--surface-glass-06', FALLBACK.surface06);
  const borderSubtle = readVar(map.colorBorder, FALLBACK.borderDefault);
  const textPrimary = readVar(map.colorText, FALLBACK.textPrimary);
  const textMuted = readVar('--text-secondary', FALLBACK.textMuted);
  const textQuaternary = readVar('--color-text-muted', FALLBACK.textQuaternary);
  const colorLink =
    brandMerge?.token.colorLink ??
    (readVar('--color-link', FALLBACK.colorLink) || readVar('--color-aqua', FALLBACK.colorLink));
  const colorLinkHover =
    brandMerge?.token.colorLinkHover ?? readVar('--color-link-hover', FALLBACK.colorLinkHover);
  const colorLinkActive =
    brandMerge?.token.colorLinkActive ?? readVar('--color-link-active', FALLBACK.colorLinkActive);
  const layoutSecondary = readVar('--color-bg-secondary', FALLBACK.layoutSecondary);
  const overlayScrim = readVar('--overlay-scrim', FALLBACK.overlayScrim);
  const tableHeaderBg = readVar('--table-header-bg', surface04);
  const tableRowHoverBg = readVar('--table-row-hover-bg', surface04);
  const colorPrimary =
    brandMerge?.token.colorPrimary ?? readVar('--primary-color', FALLBACK.primary);

  /** darkAlgorithm replaces hex text/link tokens with low-contrast rgba — restore WCAG AA hex SSOT. */
  const preserveBusinessDarkContrastTokens = ((
    mapToken: Parameters<typeof antdTheme.darkAlgorithm>[0]
  ) => ({
    ...mapToken,
    colorText: textPrimary,
    colorTextSecondary: textMuted,
    colorTextTertiary: textQuaternary,
    colorTextQuaternary: textQuaternary,
    colorTextDescription: textMuted,
    colorLink,
    colorLinkHover,
    colorLinkActive,
    ...(brandMerge?.token.colorTextLightSolid
      ? { colorTextLightSolid: brandMerge.token.colorTextLightSolid }
      : {}),
  })) as unknown as typeof antdTheme.darkAlgorithm;

  return {
    algorithm: [antdTheme.darkAlgorithm, preserveBusinessDarkContrastTokens],
    token: {
      colorPrimary,
      colorBgBase: readVar('--layout-bg', FALLBACK.layoutBg),
      colorBgContainer: readVar(map.colorBgContainer, FALLBACK.surfaceCanvas),
      colorBgLayout: readVar('--layout-bg', FALLBACK.layoutBg),
      colorBgElevated: readVar('--surface-subtle', FALLBACK.surfaceSubtle),
      colorText: textPrimary,
      colorTextSecondary: textMuted,
      colorTextTertiary: textQuaternary,
      colorTextQuaternary: textQuaternary,
      colorTextDescription: textMuted,
      colorLink,
      colorLinkHover,
      colorLinkActive,
      colorBorder: borderSubtle,
      borderRadius: readPxVar('--radius-lg', FALLBACK.radius),
      borderRadiusLG: readPxVar('--radius-xl', FALLBACK.radiusLg),
      colorSuccess: readVar(map.colorSuccess, FALLBACK.success),
      colorWarning: readVar(map.colorWarning, FALLBACK.warning),
      colorError: readVar(map.colorError, FALLBACK.error),
      controlHeight: readPxVar(map.controlHeight, FALLBACK.controlHeight),
      controlHeightSM: readPxVar(map.controlHeightSM, FALLBACK.controlHeightSm),
      controlHeightLG: readPxVar(map.controlHeightLG, FALLBACK.controlHeightLg),
      fontSize: readPxVar(map.fontSize, FALLBACK.fontSize),
      fontSizeSM: readPxVar(map.fontSizeSM, FALLBACK.fontSizeSm),
      lineHeight: readNumberVar(map.lineHeight, FALLBACK.lineHeight),
      motionDurationMid: readVar(map.motionDurationMid, FALLBACK.motionDurationMid),
      ...(brandMerge?.token ?? {}),
    },
    components: {
      Table: {
        headerBg: tableHeaderBg,
        headerColor: textPrimary,
        rowHoverBg: tableRowHoverBg,
        borderColor: borderSubtle,
        footerBg: surface04,
      },
      List: {
        headerBg: surface04,
        footerBg: surface04,
      },
      Card: {
        headerBg: surface06,
        actionsBg: surface03,
        extraColor: textMuted,
      },
      Segmented: {
        trackBg: surface03,
        itemColor: textMuted,
        itemHoverColor: textPrimary,
        itemHoverBg: surface06,
        itemSelectedBg: surface06,
        itemSelectedColor: textPrimary,
        itemActiveBg: surface04,
      },
      Steps: {
        navArrowColor: textQuaternary,
      },
      Menu: {
        itemBg: 'transparent',
        itemColor: textMuted,
        itemHoverBg: surface06,
        itemHoverColor: textPrimary,
        itemSelectedBg: surface06,
        itemSelectedColor: brandMerge?.components?.Menu?.itemSelectedColor ?? textPrimary,
        darkItemBg: 'transparent',
        darkItemColor: textMuted,
        darkItemHoverBg: surface06,
        darkItemHoverColor: textPrimary,
        darkItemSelectedBg: surface06,
        darkItemSelectedColor: brandMerge?.components?.Menu?.darkItemSelectedColor ?? textPrimary,
        popupBg: layoutSecondary,
      },
      Drawer: {
        colorBgElevated: layoutSecondary,
        colorBgMask: overlayScrim,
      },
      Typography: {
        colorTextDescription: textMuted,
      },
      Button: {
        defaultColor: textMuted,
        defaultHoverColor: textPrimary,
        textTextColor: textPrimary,
        ...(brandMerge?.components?.Button ?? {}),
      },
    },
  };
}
